import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { errorDev } from '../utils/logger.js';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

const CHART_WINDOWS = [7, 30, 90];
const WATCHLIST_ROW_HEIGHT = 72;

function fmtPrice(v) {
  if (!Number.isFinite(Number(v))) return "--";
  return `${Number(v).toLocaleString()} RWF`;
}

function fmtPct(v) {
  if (!Number.isFinite(Number(v))) return "--";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;
}

function fmtAbsolute(v) {
  if (!Number.isFinite(Number(v))) return "--";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toLocaleString()} RWF`;
}

function pctTone(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "text-gray-500 dark:text-gray-400";
  if (n > 0) return "text-green-600 dark:text-green-400";
  if (n < 0) return "text-red-600 dark:text-red-400";
  return "text-gray-500 dark:text-gray-400";
}

function directionMeta(status, t) {
  const key = status || "stable";
  const label = t(`priceStatus.${key}`);
  if (key === "falling") {
    return {
      arrow: "▼",
      className: "text-red-600 dark:text-red-400",
      badge: "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-900/60",
      label,
    };
  }
  if (key === "rising") {
    return {
      arrow: "▲",
      className: "text-green-600 dark:text-green-400",
      badge: "bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-900/60",
      label,
    };
  }
  return {
    arrow: "■",
    className: "text-gray-500 dark:text-gray-400",
    badge: "bg-gray-50 border-gray-200 dark:bg-neutral-800 dark:border-neutral-700",
    label,
  };
}

function compactCropCode(crop = "") {
  const parts = String(crop).split(" ").filter(Boolean);
  if (parts.length === 0) return "--";
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}${parts[2]?.[0] || ""}`.toUpperCase();
}

function sparkBounds(spark = []) {
  if (!Array.isArray(spark) || spark.length === 0) return { min: null, max: null };
  const vals = spark.map((p) => Number(p.y)).filter((n) => Number.isFinite(n));
  if (vals.length === 0) return { min: null, max: null };
  return { min: Math.min(...vals), max: Math.max(...vals) };
}

function miniPath(points = [], width = 110, height = 24, pad = 2) {
  const vals = points.map((p) => Number(p.y)).filter((n) => Number.isFinite(n));
  if (vals.length < 2) return "";
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;

  return points
    .map((p, i) => {
      const x = pad + (i * (width - pad * 2)) / (points.length - 1);
      const yRaw = Number(p.y);
      const y = height - pad - ((yRaw - min) / span) * (height - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

function WatchRow({ row, active, onSelect }) {
  const changeClass = pctTone(row.change30);
  const movementLabel = Number.isFinite(Number(row.change30))
    ? `${fmtPct(row.change30)} ${fmtAbsolute(row.change30Abs)}`
    : "No movement data";

  return (
    <button
      type="button"
      onClick={() => onSelect(row.cropKey || row.crop)}
      style={{ minHeight: WATCHLIST_ROW_HEIGHT }}
      aria-label={`${row.crop}, ${movementLabel}`}
      className={`w-full text-left px-3 py-3.5 sm:px-4 border-b border-gray-200/80 dark:border-neutral-800 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-600 ${
        active
          ? "bg-green-50 dark:bg-green-900/20"
          : "hover:bg-gray-50 dark:hover:bg-neutral-800/60"
      }`}
    >
      <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(120px,1fr)_minmax(140px,1.2fr)] items-center gap-3 sm:gap-5">
        <div className="min-w-0">
          <p className="text-gray-900 dark:text-gray-100 font-semibold text-sm sm:text-base leading-tight truncate">{row.crop}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm truncate">{row.category || compactCropCode(row.crop)}</p>
        </div>

        <div className="min-w-0 text-right">
          <p className="text-gray-900 dark:text-gray-100 text-sm sm:text-base font-semibold tabular-nums truncate">
            {fmtPrice(row.last)}
          </p>
        </div>

        <div className="text-right min-w-0">
          <p className={`text-sm sm:text-base font-semibold tabular-nums truncate ${changeClass}`}>{fmtPct(row.change30)}</p>
          <p className={`text-[11px] sm:text-xs tabular-nums truncate ${changeClass}`}>{fmtAbsolute(row.change30Abs)}</p>
        </div>
      </div>
    </button>
  );
}

function QuickCompareCard({ row, onSelect, className = "" }) {
  return (
    <button
      type="button"
      onClick={() => onSelect?.(row.cropKey || row.crop)}
      aria-label={`${row.crop} ${fmtPrice(row.last, row.priceUnit)} ${fmtPct(row.change30)}`}
      className={`w-[116px] sm:w-[128px] shrink-0 rounded-lg border border-gray-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2.5 py-2 text-left transition hover:bg-gray-50 dark:hover:bg-neutral-800/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 ${className}`}
    >
      <div className="flex items-center justify-between gap-1">
        <p className="text-gray-900 dark:text-gray-100 text-[11px] sm:text-xs font-semibold truncate">{compactCropCode(row.crop)}</p>
        <p className={`text-[10px] sm:text-[11px] font-semibold tabular-nums ${pctTone(row.change30)}`}>{fmtPct(row.change30)}</p>
      </div>
      <p className="mt-1 text-sm sm:text-base leading-tight font-semibold tabular-nums text-gray-900 dark:text-gray-100 truncate">{fmtPrice(row.last, row.priceUnit)}</p>
      <p className="mt-0.5 text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400 truncate">{row.crop}</p>
    </button>
  );
}

export default function FarmPulseMarketTerminal({ rows = [], windowDays = 90, onWindowChange }) {
  const { t } = useTranslation("marketAnalysis");
  const [selectedCrop, setSelectedCrop] = useState(rows[0]?.cropKey || rows[0]?.crop || null);
  const [chartWindow, setChartWindow] = useState(
    CHART_WINDOWS.includes(Number(windowDays)) ? Number(windowDays) : 90
  );
  const [isDarkMode, setIsDarkMode] = useState(false);
  const watchlistRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(620);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const sync = () => setIsDarkMode(root.classList.contains("dark"));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const next = CHART_WINDOWS.includes(Number(windowDays)) ? Number(windowDays) : 90;
    setChartWindow(next);
  }, [windowDays]);

  useEffect(() => {
    if (!rows.length) {
      setSelectedCrop(null);
      return;
    }
    const exists = rows.some((r) => (r.cropKey || r.crop) === selectedCrop);
    if (!exists) setSelectedCrop(rows[0].cropKey || rows[0].crop);
  }, [rows, selectedCrop]);

  const selected = useMemo(
    () => rows.find((r) => (r.cropKey || r.crop) === selectedCrop) || rows[0] || null,
    [rows, selectedCrop]
  );

  const compareRows = useMemo(() => {
    if (!selected) return rows.slice(0, 6);
    return rows.filter((r) => (r.cropKey || r.crop) !== (selected.cropKey || selected.crop)).slice(0, 6);
  }, [rows, selected]);

  const chartData = useMemo(() => {
    const spark = selected?.spark || [];
    if (spark.length <= 2) return spark;
    const cutoff = Date.now() - chartWindow * 86_400_000;
    const filtered = spark.filter((p) => Number(p.x) >= cutoff);
    if (filtered.length >= 2) return filtered;
    return spark.slice(-Math.max(2, Math.min(40, spark.length)));
  }, [selected, chartWindow]);

  const chartWithForecast = useMemo(() => {
    if (!selected) return chartData;
    const history = (chartData || []).map((p) => ({ ...p, forecast: null }));
    const lastPoint = history[history.length - 1];
    if (!lastPoint) return history;

    const mapPoint = (days, value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      return {
        x: Number(lastPoint.x) + days * 86_400_000,
        y: null,
        forecast: n,
      };
    };

    const f30 = mapPoint(30, selected.proj30);
    const f90 = mapPoint(90, selected.proj90);
    const f180 = mapPoint(180, selected.proj180);

    const forecastPoints = [
      { ...lastPoint, forecast: Number(lastPoint.y) },
      f30,
      f90,
      f180,
    ].filter(Boolean);

    return [...history, ...forecastPoints.slice(1)];
  }, [chartData, selected]);

  const bounds = sparkBounds(chartData);
  const selectedDirection = directionMeta(selected?.priceStatus, t);

  useEffect(() => {
    const el = watchlistRef.current;
    if (!el) return;
    const update = () => setViewportHeight(el.clientHeight || 620);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const startIndex = Math.max(0, Math.floor(scrollTop / WATCHLIST_ROW_HEIGHT) - 6);
  const visibleCount = Math.ceil(viewportHeight / WATCHLIST_ROW_HEIGHT) + 12;
  const endIndex = Math.min(rows.length, startIndex + visibleCount);
  const visibleRows = rows.slice(startIndex, endIndex);
  const topSpacer = startIndex * WATCHLIST_ROW_HEIGHT;
  const bottomSpacer = Math.max(0, (rows.length - endIndex) * WATCHLIST_ROW_HEIGHT);

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-gray-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-6 py-10 text-center text-gray-600 dark:text-gray-300">
        {t("table.noData")}
      </div>
    );
  }

  return (
    <section
      className="rounded-xl border border-gray-200/80 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 shadow-sm"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(360px,42%)_1fr]">
        <aside
          ref={watchlistRef}
          className="order-1 lg:order-1 lg:border-r border-gray-200/80 dark:border-neutral-800 lg:max-h-[620px] overflow-y-auto bg-white dark:bg-neutral-900"
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        >
          <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(120px,1fr)_minmax(140px,1.2fr)] gap-3 sm:gap-5 px-3 sm:px-4 py-2 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-neutral-800">
            <span>{t("table.product", { defaultValue: "Product" })}</span>
            <span className="text-right">{t("table.amount", { defaultValue: "Unit Price" })}</span>
            <span className="text-right">{t("table.priceChange", { defaultValue: "Price change" })}</span>
          </div>
          {topSpacer > 0 && <div style={{ height: topSpacer }} aria-hidden="true" />}
          {visibleRows.map((row) => (
            <WatchRow
              key={row.cropKey || row.crop}
              row={row}
              active={(selected?.cropKey || selected?.crop) === (row.cropKey || row.crop)}
              onSelect={setSelectedCrop}
            />
          ))}
          {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} aria-hidden="true" />}
        </aside>

        <div className="order-2 lg:order-2 min-w-0 p-3 sm:p-4 lg:p-5 flex flex-col gap-3 sm:gap-4">
          <div className="min-w-0 rounded-xl border border-gray-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 sm:p-4">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 font-medium">{selected?.crop || "--"}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{selected?.category || compactCropCode(selected?.crop)}</p>
                <span className={`mt-1 inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-xs font-semibold ${selectedDirection.className} ${selectedDirection.badge}`}>
                  <span>{selectedDirection.arrow}</span>
                  <span>{selectedDirection.label}</span>
                </span>
              </div>
              <div className="text-right">
                <p className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100">{fmtPrice(selected?.last)}</p>
                <p className={`text-xs sm:text-sm font-semibold ${pctTone(selected?.change30)}`}>{fmtPct(selected?.change30)}</p>
              </div>
            </div>

            <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-neutral-700 bg-gray-100/80 dark:bg-neutral-800 p-1">
                {CHART_WINDOWS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      setChartWindow(d);
                      onWindowChange?.(d);
                    }}
                    className={`px-2.5 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full transition ${
                      chartWindow === d
                        ? "bg-green-700 text-white"
                        : "text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-neutral-700"
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("terminal.liveFromData", { defaultValue: "Live updates from market data" })}
              </p>
            </div>

            <div className="h-[220px] sm:h-[250px] md:h-[280px] lg:h-[300px] xl:h-[320px] min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartWithForecast} margin={{ left: 6, right: 8, top: 8, bottom: 8 }}>
                  <defs>
                    <linearGradient id="terminalArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={isDarkMode ? "#4ade80" : "#16a34a"} stopOpacity="0.26" />
                      <stop offset="100%" stopColor={isDarkMode ? "#4ade80" : "#16a34a"} stopOpacity="0.04" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke={isDarkMode ? "#3f3f46" : "#e5e7eb"}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="x"
                    tickFormatter={(v) => {
                      try {
                        const d = new Date(v);
                        if (chartWindow >= 90) return `${d.getDate()}/${d.getMonth() + 1}`;
                        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                      } catch (err) {
                        errorDev("Operation failed:", err);
                        return "";
                      }
                    }}
                    stroke={isDarkMode ? "#a3a3a3" : "#6b7280"}
                    tick={{ fill: isDarkMode ? "#a3a3a3" : "#6b7280", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke={isDarkMode ? "#a3a3a3" : "#6b7280"}
                    tick={{ fill: isDarkMode ? "#a3a3a3" : "#6b7280", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                    domain={["auto", "auto"]}
                  />
                  <Tooltip
                    cursor={{ stroke: isDarkMode ? "#737373" : "#9ca3af", strokeDasharray: "4 4" }}
                    labelFormatter={(v) => {
                      try {
                        return new Date(v).toLocaleString();
                      } catch (err) {
                        errorDev("Operation failed:", err);
                        return "";
                      }
                    }}
                    formatter={(v) => [fmtPrice(v), t("table.lastPrice", { defaultValue: "Price" })]}
                    contentStyle={{
                      background: isDarkMode ? "#171717" : "#ffffff",
                      border: `1px solid ${isDarkMode ? "#3f3f46" : "#e5e7eb"}`,
                      color: isDarkMode ? "#f3f4f6" : "#111827",
                      borderRadius: 10,
                    }}
                  />
                  {Number.isFinite(Number(selected?.last)) && (
                    <ReferenceLine
                      y={Number(selected.last)}
                      stroke={isDarkMode ? "#d4d4d8" : "#6b7280"}
                      strokeDasharray="4 4"
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="y"
                    stroke={isDarkMode ? "#4ade80" : "#16a34a"}
                    strokeWidth={2.5}
                    fill="url(#terminalArea)"
                    dot={false}
                    activeDot={{ r: 3.5, fill: isDarkMode ? "#86efac" : "#16a34a" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    stroke={selected?.proj30 >= selected?.last ? (isDarkMode ? "#86efac" : "#16a34a") : (isDarkMode ? "#fca5a5" : "#dc2626")}
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="6 4"
                    connectNulls
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="min-w-0">
            <p className="text-gray-900 dark:text-gray-100 font-semibold text-base sm:text-lg mb-2">{t("quickCompare", { defaultValue: "Quick Compare" })}</p>
            <div className="flex flex-row gap-2 sm:gap-2.5 overflow-x-auto pb-2 no-scrollbar">
              {compareRows.map((r) => (
                <QuickCompareCard key={r.cropKey || r.crop} row={r} onSelect={setSelectedCrop} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
              <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">
                {t("windowRange", { defaultValue: `${chartWindow}-Day Range`, days: chartWindow })}
              </p>
              <p className="mt-1.5 text-gray-900 dark:text-gray-100 text-lg sm:text-xl md:text-2xl font-semibold">
                {fmtPrice(bounds.min)} - {fmtPrice(bounds.max)}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
              <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">
                {t("forecast1m", { defaultValue: "1M Forecast" })}
              </p>
              <p className="mt-1.5 text-gray-900 dark:text-gray-100 text-lg sm:text-xl md:text-2xl font-semibold">
                {fmtPrice(selected?.proj30)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-2.5 border-t border-gray-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm gap-2 sm:gap-3">
        <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm break-words">
          {t("terminal.footer", { defaultValue: "Prices are shown in RWF. Tap an item to inspect its trend and compare products." })}
        </p>
        <span className="self-end sm:self-auto text-xs sm:text-sm text-green-700 dark:text-green-300 whitespace-nowrap">
          {t("terminal.liveBadge", { defaultValue: "Live data" })}
        </span>
      </div>
    </section>
  );
}
